import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Pagination from "@/components/base/Pagination";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { works } from "@/mocks/works";

const PAGE_SIZE = 12;
const typeOptions = ["全部", "电视剧", "电影"];
const sortOptions = [
  { key: "latest", label: "最新排序" },
  { key: "popular", label: "热门排序" },
];

export default function Works() {
  useDocumentTitle(
    "杨幂影视作品 - 明星资讯平台",
    "杨幂影视作品库，包括杨幂参演的电视剧与电影作品全集，支持按类型、年份筛选与最新、热门排序浏览。",
    "杨幂影视作品,杨幂电视剧,杨幂电影,杨幂作品全集",
  );

  const [params, setParams] = useSearchParams();
  const initialType = params.get("type") || "全部";
  const [type, setType] = useState(initialType);
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  const years = useMemo(() => {
    const set = Array.from(new Set(works.map((w) => w.year))).sort((a, b) => b - a);
    return ["全部", ...set.map(String)];
  }, []);

  const filtered = useMemo(() => {
    let list = works.filter((w) => (type === "全部" ? true : w.type === type));
    if (year !== "全部") list = list.filter((w) => String(w.year) === year);
    if (sort === "latest") list = [...list].sort((a, b) => b.year - a.year);
    else list = [...list].sort((a, b) => (b.tags.includes("热门") ? 1 : 0) - (a.tags.includes("热门") ? 1 : 0));
    return list;
  }, [type, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setType("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
    setParams({});
  };

  const changeType = (t: string) => {
    setType(t);
    setPage(1);
    setParams(t === "全部" ? {} : { type: t });
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "影视作品" }]} />

      <div className="mt-5 mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">杨幂影视作品库</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl">
          收录杨幂参演的电视剧与电影作品，可按类型、年份筛选，并支持最新与热门排序，点击卡片查看作品详情。
        </p>
      </div>

      <div className="bg-background-100/70 border border-background-200 rounded-lg p-4 md:p-5 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
          <div className="flex items-center gap-3">
            <span className="text-sm font-label text-foreground-600 whitespace-nowrap">类型</span>
            <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 border border-background-200">
              {typeOptions.map((t) => (
                <button
                  key={t}
                  onClick={() => changeType(t)}
                  className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                    t === type ? "bg-primary-500 text-background-50 font-semibold" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-label text-foreground-600 whitespace-nowrap">年份</span>
            <select
              value={year}
              onChange={(e) => { setYear(e.target.value); setPage(1); }}
              className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y === "全部" ? "全部年份" : `${y} 年`}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3 lg:ml-auto">
            <span className="text-sm font-label text-foreground-600 whitespace-nowrap">排序</span>
            <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 border border-background-200">
              {sortOptions.map((s) => (
                <button
                  key={s.key}
                  onClick={() => { setSort(s.key); setPage(1); }}
                  className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                    s.key === sort ? "bg-secondary-500 text-background-50 font-semibold" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {pageList.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {pageList.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
            <i className="ri-inbox-line text-3xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-800">暂无符合条件的作品</p>
          <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置查看全部作品。</p>
          <button
            onClick={resetFilters}
            className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}